import React, { useState, useMemo, useEffect, useContext } from 'react';
import '../css/ReturnsManagement.css';
import { AuthContext } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { returnsAPI, vendorsAPI, clientsAPI, getProducts, getRawMaterials, getBranches } from '../utils/api';

const ROWS_PER_PAGE = 25;

/* -------------------- COMPONENT -------------------- */
const ReturnsManagement = () => {
    const { user } = useContext(AuthContext);

    // Normalize user role
    const role = (user?.role || '').toString().toLowerCase();
    const isAdmin = role === 'admin';
    const isSalesManager = role.includes('sales');
    const isPurchaseManager = role.includes('purchase');
    const isNormalUser = role === 'normal_user';

    // Determine initial tab
    const [activeTab, setActiveTab] = useState(() => {
        if (isAdmin) return 'sales';
        if (isSalesManager) return 'sales';
        if (isPurchaseManager) return 'purchase';
        return 'sales'; // Fallback
    });
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedParty, setSelectedParty] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [applyFilter, setApplyFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);

    // Modal states
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showReturnFormModal, setShowReturnFormModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');

    // Data states
    const [returnsList, setReturnsList] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [branches, setBranches] = useState([]);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [error, setError] = useState(null);
    const [customerError, setCustomerError] = useState(null);

    const [returnForm, setReturnForm] = useState({
        transaction_type: 'cash',
        transaction_date: new Date().toISOString().split('T')[0],
        product: '',
        branch: '',
        quantity: '',
        unit_price: '',
        discount: '',
        with_gst: true,
        amount: '',
        total_amount: '',
        amount_paid: '',
        reference_number: '',
        notes: '',
    });

    const GST_RATE = 0.18;

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchReturns(),
                fetchCustomers(),
                fetchVendors(),
                fetchProducts(),
                fetchRawMaterials(),
                fetchBranches()
            ]);
        } catch (err) {
            // console.error("Error loading initial data:", err);
            setError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            // Handle both direct array and object-wrapped responses
            const productList = Array.isArray(response) ? response : (response.products || response.data || []);
            setProducts(productList);
        } catch (err) {
            // console.error("Error fetching products:", err);
        }
    };

    const fetchRawMaterials = async () => {
        try {
            const response = await getRawMaterials();
            // Handle both direct array and object-wrapped responses
            const materialList = Array.isArray(response) ? response : (response.rawMaterials || response.data || []);
            setRawMaterials(materialList);
        } catch (err) {
            // console.error("Error fetching raw materials:", err);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await getBranches();
            // Handle both direct array and object-wrapped responses
            const branchList = Array.isArray(response) ? response : (response.branches || response.data || []);
            setBranches(branchList);
        } catch (err) {
            // console.error("Error fetching branches:", err);
        }
    };

    const fetchReturns = async () => {
        try {
            const response = await returnsAPI.getAll();
            setReturnsList(response.data || []);
        } catch (err) {
            // console.error("Error fetching returns:", err);
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await vendorsAPI.getAll();
            const vendorList = response.data?.data || response.data || [];

            // Standardize vendor structure
            const transformedVendors = vendorList.map(v => ({
                id: v.id || v.vendor_id,
                name: v.name || v.vendor_name || 'Unknown Vendor',
                phone: v.phone || v.mobile || '',
                email: v.email || '',
                address: v.address || '',
                gst_no: v.gst_no || v.gst_number || '',
                status: v.account_status || 'active',
                type: 'Vendor'
            }));
            setVendors(transformedVendors);
        } catch (err) {
            // console.error("Error fetching vendors:", err);
        }
    };

    // Filter customers/vendors based on search
    useEffect(() => {
        const sourceData = activeTab === 'sales' ? customers : vendors;

        if (sourceData.length > 0 && customerSearch) {
            const filtered = sourceData.filter(item => {
                const searchLower = customerSearch.toLowerCase();
                return (
                    item.name?.toLowerCase().includes(searchLower) ||
                    item.phone?.includes(customerSearch) ||
                    item.email?.toLowerCase().includes(searchLower) ||
                    (item.id && item.id.toString().includes(customerSearch))
                );
            });
            setFilteredCustomers(filtered);
        } else {
            setFilteredCustomers(sourceData);
        }
    }, [customerSearch, customers, vendors, activeTab]);

    const fetchCustomers = async () => {
        try {
            setLoadingCustomers(true);
            setCustomerError(null);

            // Fetch customers from API
            const response = await returnsAPI.getByCustomer();

            // Handle different response formats
            let customerList = [];
            if (response.data && Array.isArray(response.data)) {
                customerList = response.data;
            } else if (response.data && response.data.customers) {
                customerList = response.data.customers;
            } else if (response.data && response.data.data) {
                customerList = response.data.data;
            }

            // Transform customer data to ensure consistent structure
            const transformedCustomers = customerList.map(customer => ({
                id: customer.id || customer.customer_id,
                customer_id: customer.customer_id || customer.id,
                name: customer.name || customer.customer_name || 'Unknown Customer',
                phone: customer.phone || customer.contact || customer.mobile || '',
                email: customer.email || '',
                address: customer.address || '',
                gst_no: customer.gst_no || customer.gst_number || '',
                status: customer.status || 'active',
                type: 'Customer'
            }));

            setCustomers(transformedCustomers);
            setFilteredCustomers(transformedCustomers);
        } catch (error) {
            // console.error('Error fetching customers:', error);
            setCustomerError('Failed to load customers. Please try again.');

            // Fallback to sample data if API fails
            const sampleCustomers = [
                { id: 1, customer_id: 101, name: 'ABC Traders', phone: '9876543210', email: 'abc@traders.com' },
                { id: 2, customer_id: 102, name: 'XYZ Enterprises', phone: '9876543211', email: 'xyz@enterprises.com' },
                { id: 3, customer_id: 103, name: 'MNO Suppliers', phone: '9876543212', email: 'mno@suppliers.com' },
            ];
            setCustomers(sampleCustomers);
            setFilteredCustomers(sampleCustomers);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const recalcTotal = form => {
        const qty = parseFloat(form.quantity) || 0;
        const price = parseFloat(form.unit_price) || 0;
        const discountVal = parseFloat(form.discount) || 0;
        
        const subtotal = qty * price;
        const discounted = subtotal - discountVal;
        const gstAmount = form.with_gst ? discounted * GST_RATE : 0;
        const total = discounted + gstAmount;

        return {
            ...form,
            amount: discounted,
            total_amount: Number(total.toFixed(2)),
        };
    };

    const formatCurrency = value =>
        `₹ ${Number(value || 0).toFixed(2)}`;

    const handleSaveSale = async () => {
        try {
            const returnType = activeTab === 'sales' ? 'Sales' : 'Purchase';
            // console.log(`Saving ${returnType.toLowerCase()} return:`, {
            //     customer_id: selectedCustomer.id,
            //     customer_name: selectedCustomer.name,
            //     ...returnForm
            // });

            // Prepare payload for backend
            const saveData = {
                return_number: returnForm.reference_number || `RET-${Date.now()}`,
                sale_id: activeTab === 'sales' ? (returnForm.reference_number || null) : null,
                purchase_id: activeTab !== 'sales' ? (returnForm.reference_number || null) : null,
                client_vendor_id: selectedCustomer.id || selectedCustomer.customer_id,
                client_vendor_type: activeTab === 'sales' ? 'Customer' : 'Vendor',
                date_initiated: returnForm.transaction_date,
                status: 'Pending',

                // Detailed Fields
                product: returnForm.product,
                quantity: returnForm.quantity,
                unit_price: returnForm.unit_price,
                discount: returnForm.discount,
                with_gst: returnForm.with_gst,
                amount: returnForm.amount, // Net amount
                total_amount: returnForm.total_amount, // Gross amount (mapped to refund_amount)
                refund_amount: returnForm.total_amount,
                amount_paid: returnForm.amount_paid,
                transaction_type: returnForm.transaction_type,
                notes: returnForm.notes,
                branch: returnForm.branch,

                // Now we save the CLEAN notes in reason, as Party ID is preserved in client_vendor_id column
                reason: returnForm.notes || 'N/A'
            };

            await returnsAPI.create(saveData);

            setShowReturnFormModal(false);
            setSelectedCustomer(null);

            // Reset form
            setReturnForm({
                transaction_type: 'cash',
                transaction_date: new Date().toISOString().split('T')[0],
                product: '',
                branch: '',
                quantity: '',
                unit_price: '',
                discount: '',
                with_gst: true,
                amount: '',
                total_amount: '',
                amount_paid: '',
                reference_number: '',
                notes: '',
            });

            // Show success message
            alert(`${returnType} return saved successfully!`);

            // Refresh list to show new item
            fetchReturns();

        } catch (error) {
            // console.error('Error saving return:', error);
            alert(`Failed to save ${activeTab === 'sales' ? 'sales' : 'purchase'} return. Please try again.`);
        }
    };

    // Helper to resolve name from ID
    const resolvePartyName = (id, type) => {
        if (type === 'Customer') {
            const found = customers.find(c => c.id == id || c.customer_id == id);
            return found ? found.name : `Customer ID: ${id}`;
        } else {
            const found = vendors.find(v => v.id == id);
            return found ? found.name : `Vendor ID: ${id}`;
        }
    };

    /* -------------------- FORMATTERS -------------------- */
    const formatCurrencyWithoutSymbol = (amount) => {
        if (!amount) return '0.00';
        const num = parseFloat(amount);
        return num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDateDDMMYYYY = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    /* -------------------- PDF EXPORT -------------------- */
    const exportToPDF = () => {
        const dataToExport = filteredData;
        if (!dataToExport.length) {
            alert('No returns data to export!');
            return;
        }

        try {
            const doc = new jsPDF('landscape');
            const title = activeTab === 'sales' ? 'Sales Returns Report' : 'Purchase Returns Report';
            const partyLabel = activeTab === 'sales' ? 'Customer:' : 'Vendor:';

            // ---------- HEADER ----------
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 128); // Navy Blue
            doc.text('OMEGA', 14, 20);

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text(title, 14, 30);

            doc.setDrawColor(0, 0, 128);
            doc.line(14, 35, 280, 35);

            // Filter Infos
            let yPos = 45;
            doc.setFontSize(11);

            // If filtering by specific party
            if (selectedParty) {
                doc.setFont('helvetica', 'bold');
                doc.text(partyLabel, 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(selectedParty, 40, yPos);
            } else {
                doc.setFont('helvetica', 'bold');
                doc.text('Report Type:', 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(activeTab === 'sales' ? 'All Sales Returns' : 'All Purchase Returns', 40, yPos);
            }

            // Date Range
            doc.setFont('helvetica', 'bold');
            doc.text('Report Date:', 200, yPos);
            doc.setFont('helvetica', 'normal');
            doc.text(formatDateDDMMYYYY(new Date()), 225, yPos);

            yPos += 8;
            if (fromDate || toDate) {
                doc.setFont('helvetica', 'bold');
                doc.text('Period:', 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(`${fromDate ? formatDateDDMMYYYY(fromDate) : 'Start'} to ${toDate ? formatDateDDMMYYYY(toDate) : 'End'}`, 40, yPos);
                yPos += 10;
            } else {
                yPos += 2;
            }

            // ---------- TABLE DATA ----------
            const tableData = dataToExport.map(item => [
                formatDateDDMMYYYY(item.date),
                item.reference || '-',
                item.partyName || '-',
                item.notes || item.product || '-', // Mapping 'Reason for Return' to Notes/Product logic or raw reason? User said "reason for return column". Our schema sends 'notes' as clean reason.
                item.product || '-',
                item.quantity || '0',
                formatCurrencyWithoutSymbol(item.unit_price || 0),
                formatCurrencyWithoutSymbol(item.discount || 0),
                item.with_gst || item.gst === '18%' ? 'Yes' : 'No',
                formatCurrencyWithoutSymbol(item.amount || 0), // Net
                formatCurrencyWithoutSymbol(item.total_amount || 0) // Total/Refund
            ]);

            // Totals
            const totals = {
                quantity: dataToExport.reduce((s, i) => s + (parseInt(i.quantity) || 0), 0),
                amount: dataToExport.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
                total_amount: dataToExport.reduce((s, i) => s + (parseFloat(i.total_amount) || 0), 0)
            };

            autoTable(doc, {
                startY: yPos + 5,
                head: [[
                    'Date', 'Ref No', 'Party Name', 'Reason', 'Product',
                    'Qty', 'Unit Price', 'Discount', 'GST', 'Net Amt', 'Refund Amt'
                ]],
                body: tableData,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [0, 0, 128], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 35 }, // Reason
                    4: { cellWidth: 30 }, // Product
                    5: { cellWidth: 12, halign: 'right' },
                    6: { cellWidth: 20, halign: 'right' },
                    7: { cellWidth: 18, halign: 'right' },
                    8: { cellWidth: 12, halign: 'center' },
                    9: { cellWidth: 22, halign: 'right' },
                    10: { cellWidth: 22, halign: 'right' }
                },
                margin: { left: 14, right: 14 },
                didDrawPage: (data) => {
                    // Footer is better in didDrawPage to appear on all pages
                }
            });

            // ---------- SUMMARY ----------
            const finalY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Qty: ${totals.quantity}`, 14, finalY);
            doc.text(`Total Net: ${formatCurrencyWithoutSymbol(totals.amount)}`, 80, finalY);
            doc.text(`Total Refund: ${formatCurrencyWithoutSymbol(totals.total_amount)}`, 140, finalY);

            // ---------- FOOTER ----------
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(100);
                doc.text(
                    `Page ${i} of ${pageCount} • Generated by Omega Sales System`,
                    14,
                    doc.internal.pageSize.height - 10
                );
            }

            doc.save(`${title.replace(/ /g, '_')}_${new Date().getTime()}.pdf`);

        } catch (error) {
            // console.error('PDF Export Error:', error);
            alert('Failed to generate PDF');
        }
    };

    /* -------------------- FILTER & DATA PROCESSING -------------------- */
    const processedData = useMemo(() => {
        return returnsList
            .map(item => {
                // Determine Party ID and Type
                // Priority: client_vendor_id (new schema) -> sale_id/purchase_id -> Fallback
                let partyId = item.client_vendor_id || item.sale_id || item.purchase_id;
                let type = 'Unknown';

                if (item.client_vendor_type) {
                    type = item.client_vendor_type;
                } else if (item.sale_id) {
                    type = 'Customer';
                } else if (item.purchase_id) {
                    type = 'Vendor';
                } else {
                    // For legacy records without new columns, try legacy regex or default to active context
                    const match = item.reason && item.reason.match(/Party ID:\s*([a-f0-9-]+)/i);
                    if (match && match[1]) {
                        partyId = match[1];
                        type = 'Customer'; // Assumption for legacy regex
                    } else {
                        // Default fallback
                        type = activeTab === 'sales' ? 'Customer' : 'Vendor';
                    }
                }

                return { ...item, derivedType: type, resolvedPartyId: partyId };
            })
            .filter(item => {
                if (activeTab === 'sales') {
                    return item.derivedType === 'Customer';
                } else {
                    return item.derivedType === 'Vendor';
                }
            })
            .map(item => {
                return {
                    ...item,
                    partyName: resolvePartyName(item.resolvedPartyId, item.derivedType),
                    date: item.date ? new Date(item.date).toISOString().split('T')[0] : (item.date_initiated ? new Date(item.date_initiated).toISOString().split('T')[0] : ''),
                    reference: item.return_number || item.return_id || String(item.id),
                    // Use new fields if available, otherwise fallback
                    product: item.product || item.reason || item.product_ids || 'Unknown Product',
                    quantity: item.quantity || 0,
                    unit_price: item.unit_price ? Number(item.unit_price) : 0,
                    discount: item.discount ? Number(item.discount) : 0,
                    gst: item.with_gst ? '18%' : '0%', // Simplified from boolean
                    amount: item.amount ? Number(item.amount) : 0,
                    amount_paid: item.amount_paid ? Number(item.amount_paid) : 0,
                    total_amount: item.refund_amount ? Number(item.refund_amount) : (item.total_amount ? Number(item.total_amount) : 0)
                };
            });
    }, [returnsList, activeTab, customers, vendors]);

    const filteredData = useMemo(() => {
        // if (!applyFilter) return processedData; // Removed to allow immediate filtering

        return processedData.filter(row => {
            const rowDate = new Date(row.date);

            if (fromDate && rowDate < new Date(fromDate)) return false;
            if (toDate && rowDate > new Date(toDate)) return false;

            if (selectedParty) {
                if (row.partyName !== selectedParty) return false;
            }

            if (selectedProduct) {
                if (row.product !== selectedProduct) return false;
            }

            return true;
        });
    }, [applyFilter, processedData, fromDate, toDate, selectedParty]);

    // Combine logic for display data
    const displayData = useMemo(() => {
        return filteredData;
    }, [filteredData]);

    /* -------------------- PAGINATION -------------------- */
    const totalPages = Math.ceil(displayData.length / ROWS_PER_PAGE);

    const paginatedData = displayData.slice(
        (currentPage - 1) * ROWS_PER_PAGE,
        currentPage * ROWS_PER_PAGE
    );

    /* -------------------- TAB CHANGE -------------------- */
    const handleTabChange = tab => {
        setActiveTab(tab);
        setCurrentPage(1);
        setFromDate('');
        setToDate('');
        setSelectedParty('');
        setSelectedProduct('');
        setApplyFilter(false);
        setSelectedIds([]);
    };

    const handleFetchData = async () => {
        setCurrentPage(1);
        setLoading(true);

        try {
            const params = {};

            // 1. Client ID Filter
            let partyId = null;
            if (activeTab === 'sales') {
                const party = customers.find(c => c.name === selectedParty);
                if (party) partyId = party.id || party.customer_id;
            } else {
                const party = vendors.find(v => v.name === selectedParty);
                if (party) partyId = party.id || party.vendor_id;
            }

            if (selectedParty && partyId) {
                params.client_id = partyId;
                params.client_type = activeTab === 'sales' ? 'Customer' : 'Vendor';
            }

            // 2. Date Filter (Server-side)
            if (fromDate) {
                params.startDate = fromDate;
            }
            if (toDate) params.endDate = toDate;

            // console.log("Fetching Returns with params:", params);
            // console.log("Selected Party:", selectedParty);
            // if (activeTab === 'sales') console.log("Found Customer:", customers.find(c => c.name === selectedParty));

            // Fetch from backend's returns table with filters
            const response = await returnsAPI.getAll(params);
            setReturnsList(response.data || []);
            setApplyFilter(true);

        } catch (err) {
            // console.error("Error fetching returns:", err);
            alert("Failed to fetch returns data");
        } finally {
            setLoading(false);
        }
    };

    // Form handlers
    const handleFormChange = (field, value) => {
        const updatedForm = { ...returnForm, [field]: value };
        if (['quantity', 'unit_price', 'discount', 'with_gst'].includes(field)) {
            const recalculated = recalcTotal(updatedForm);
            setReturnForm(recalculated);
        } else {
            setReturnForm(updatedForm);
        }
    };

    // Customer selection handlers
    const handleAddSaleReturn = () => {
        setCustomerSearch(''); // Clear previous search
        setShowCustomerModal(true);
    };

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerModal(false);
        setShowReturnFormModal(true);
    };

    const handleRefreshCustomers = () => {
        fetchCustomers();
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(paginatedData.map(r => r.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleDeleteSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected return(s)?`)) return;

        try {
            setLoading(true);
            for (const id of selectedIds) {
                await returnsAPI.delete(id);
            }
            setSelectedIds([]);
            fetchReturns();
            alert('Selected returns deleted successfully');
        } catch (err) {
            alert('Failed to delete some returns');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="returns-container">
            <h2 className="returns-title">Returns Management</h2>

            {/* ---------- TABS ---------- */}
            <div className="returns-tabs">
                {(isAdmin || isSalesManager || isNormalUser) && (
                    <button
                        className={`returns-tab ${activeTab === 'sales' ? 'active' : ''}`}
                        onClick={() => handleTabChange('sales')}
                    >
                        Sales Returns
                    </button>
                )}

                {(isAdmin || isPurchaseManager || isNormalUser) && (
                    <button
                        className={`returns-tab ${activeTab === 'purchase' ? 'active' : ''}`}
                        onClick={() => handleTabChange('purchase')}
                    >
                        Purchase Returns
                    </button>
                )}
            </div>

            {/* ---------- ACTION BAR ---------- */}
            <div className="returns-actions">
                <div className="filter-group">
                    <label>From</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                    />

                    <label>To</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                    />

                    <label>{activeTab === 'sales' ? 'Customer' : 'Vendor'}</label>
                    <div className="customer-select-container">
                        <select
                            className="customer-dropdown"
                            value={selectedParty}
                            onChange={e => setSelectedParty(e.target.value)}
                        >
                            <option value="">All {activeTab === 'sales' ? 'Customers' : 'Vendors'}</option>
                            {activeTab === 'sales'
                                ? customers.map(customer => (
                                    <option key={customer.id || customer.customer_id} value={customer.name}>
                                        {customer.name}
                                    </option>
                                ))
                                : vendors.map(vendor => (
                                    <option key={vendor.id || vendor.vendor_id} value={vendor.name}>
                                        {vendor.name}
                                    </option>
                                ))
                            }
                        </select>
                        {loading && <div className="dropdown-spinner"></div>}
                    </div>

                    <label>{activeTab === 'sales' ? 'Product' : 'Raw Material'}</label>
                    <div className="customer-select-container">
                        <select
                            className="customer-dropdown"
                            value={selectedProduct}
                            onChange={e => setSelectedProduct(e.target.value)}
                        >
                            <option value="">All {activeTab === 'sales' ? 'Products' : 'Raw Materials'}</option>
                            {activeTab === 'sales'
                                ? products.map(p => (
                                    <option key={p.id || p.product_id || Math.random()} value={p.name || p.product_name || p.product}>
                                        {p.name || p.product_name || p.product}
                                    </option>
                                ))
                                : rawMaterials.map(m => (
                                    <option key={m.id || m.material_id || Math.random()} value={m.name || m.material_name || m.raw_material_name}>
                                        {m.name || m.material_name || m.raw_material_name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>

                    <button
                        className="fetch-btn"
                        onClick={handleFetchData}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Fetch'}
                    </button>

                    <button
                        className="btn btn-secondary"
                        style={{ marginLeft: '10px' }}
                        onClick={() => {
                            setFromDate('');
                            setToDate('');
                            setCustomerSearch('');
                            setSelectedParty('');
                            setSelectedProduct('');
                            setApplyFilter(false);
                            // Reset to simple view
                        }}
                    >
                        Clear
                    </button>

                    <button
                        className="btn btn-success"
                        style={{ marginLeft: '10px' }}
                        onClick={exportToPDF}
                        disabled={loading || filteredData.length === 0}
                    >
                        Export PDF
                    </button>
                </div>

                {!isNormalUser && (
                    <button
                        className="add-return-btn"
                        onClick={handleAddSaleReturn}
                    >
                        + Add {activeTab === 'sales' ? 'Sales' : 'Purchase'} Return
                    </button>
                )}
            </div>

            {/* ---------- TABLE ---------- */}
            {!isNormalUser && selectedIds.length > 0 && (
                <div style={{ padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', border: '1px solid #eee', borderRadius: '6px', marginBottom: '10px' }}>
                    <span style={{ fontWeight: '500', color: '#555' }}>{selectedIds.length} selected</span>
                    <button
                        onClick={handleDeleteSelected}
                        disabled={loading}
                        style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                    >
                        <i className="bi bi-trash"></i>Delete Selected
                    </button>
                </div>
            )}
            <div className="returns-table-wrapper">
                <table className="returns-table">
                    <thead>
                        <tr>
                            {!isNormalUser && (
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={paginatedData.length > 0 && selectedIds.length === paginatedData.length}
                                    />
                                </th>
                            )}
                            <th>Date</th>
                            <th>Invoice No</th>
                            <th>{activeTab === 'sales' ? 'Customer' : 'Vendor'}</th>
                            <th>Type</th>
                            <th>{activeTab === 'sales' ? 'Product' : 'Raw Material'}</th>
                            <th>Branch</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Discount</th>
                            <th>GST</th>
                            <th>Total Amount</th>
                            <th>Amount Paid</th>
                            <th>Balance</th>
                            <th>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={isNormalUser ? 14 : 15} className="no-data">
                                    {loading ? 'Loading data...' : 'No records found'}
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map(row => (
                                <tr key={row.id} className={selectedIds.includes(row.id) ? 'selected-row' : ''}>
                                    {!isNormalUser && (
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(row.id)}
                                                onChange={() => handleSelectRow(row.id)}
                                            />
                                        </td>
                                    )}
                                    <td>{row.date}</td>
                                    <td>{row.reference}</td>
                                    <td>{row.partyName}</td>
                                    <td>{row.client_vendor_type}</td>
                                    <td>{row.product}</td>
                                    <td>{row.branch || '-'}</td>
                                    <td>{row.quantity}</td>
                                    <td>{typeof row.unit_price === 'number' && row.unit_price > 0 ? row.unit_price.toFixed(2) : '-'}</td>
                                    <td>{row.discount !== undefined && row.discount > 0 ? Number(row.discount).toFixed(2) : '-'}</td>
                                    <td>{row.gst || '-'}</td>
                                    <td>{row.total_amount ? Number(row.total_amount).toFixed(2) : '-'}</td>
                                    <td>{row.amount_paid ? Number(row.amount_paid).toFixed(2) : '-'}</td>
                                    <td>{row.balance !== undefined ? Number(row.balance).toFixed(2) : '-'}</td>
                                    <td>{row.notes || row.product || '-'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ---------- STEP 1: PARTY SELECTION MODAL ---------- */}
            {
                showCustomerModal && (
                    <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
                        <div className="modal-content customer-modal" onClick={(e) => e.stopPropagation()}>
                            {/* Modal Header */}
                            <div className="modal-header">
                                <h3>Select {activeTab === 'sales' ? 'Customer' : 'Vendor'}</h3>
                                <div className="modal-header-actions">
                                    <button
                                        className="modal-close"
                                        onClick={() => setShowCustomerModal(false)}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body">
                                {/* Search Bar */}
                                <div className="customer-search-container">
                                    <input
                                        type="text"
                                        className="customer-search-input"
                                        placeholder={`Search by name, phone, email, or ${activeTab === 'sales' ? 'customer' : 'vendor'} ID...`}
                                        value={customerSearch}
                                        onChange={(e) => setCustomerSearch(e.target.value)}
                                        disabled={loadingCustomers}
                                    />
                                    <div className="search-icon">🔍</div>
                                </div>

                                {/* Loading State */}
                                {loadingCustomers ? (
                                    <div className="loading-state">
                                        <div className="spinner"></div>
                                        <p>Loading customers...</p>
                                    </div>
                                ) : customerError ? (
                                    <div className="error-state">
                                        <p className="error-message">{customerError}</p>
                                        <button
                                            className="retry-btn"
                                            onClick={handleRefreshCustomers}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="customer-count">
                                            {filteredCustomers.length} {activeTab === 'sales' ? 'customer' : 'vendor'}{filteredCustomers.length !== 1 ? 's' : ''} found
                                        </div>

                                        {/* Customer/Vendor List */}
                                        <div className="customer-list-container">
                                            {filteredCustomers.length === 0 ? (
                                                <div className="no-customers-found">
                                                    {customerSearch
                                                        ? `No ${activeTab === 'sales' ? 'customers' : 'vendors'} found for "${customerSearch}"`
                                                        : `No ${activeTab === 'sales' ? 'customers' : 'vendors'} available`
                                                    }
                                                </div>
                                            ) : (
                                                filteredCustomers.map(customer => (
                                                    <div
                                                        key={customer.id || customer.customer_id}
                                                        className="customer-card"
                                                        onClick={() => handleCustomerSelect(customer)}
                                                    >
                                                        <div className="customer-info">
                                                            <div className="customer-name-only">
                                                                {customer.name}
                                                            </div>
                                                            {customer.address && (
                                                                <div className="customer-sub-info" style={{ fontSize: '0.85em', color: '#666' }}>
                                                                    📍 {customer.address}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="customer-select-arrow">→</div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* Add New Option */}
                                        <div className="add-new-customer-section">
                                            <div className="divider">
                                                <span>OR</span>
                                            </div>
                                            <button
                                                className="add-new-customer-btn"
                                                onClick={() => {
                                                    alert(`Add new ${activeTab === 'sales' ? 'customer' : 'vendor'} functionality would go here`);
                                                }}
                                            >
                                                + Add New {activeTab === 'sales' ? 'Customer' : 'Vendor'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer">
                                <button
                                    className="btn btn-cancel"
                                    onClick={() => setShowCustomerModal(false)}
                                    disabled={loadingCustomers}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ---------- STEP 2: SALES RETURN FORM MODAL ---------- */}
            {
                showReturnFormModal && selectedCustomer && (
                    <div className="modal-overlay" onClick={() => setShowReturnFormModal(false)}>
                        <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
                            {/* Modal Header with Customer Info */}
                            <div className="modal-header">
                                <div className="customer-header-info">
                                    <h3>Add {activeTab === 'sales' ? 'Sales' : 'Purchase'} Return</h3>
                                    {selectedCustomer && (
                                        <div className="selected-customer-info">
                                            <span className="customer-tag">
                                                📋 {selectedCustomer.name}
                                                {selectedCustomer.id && (
                                                    <span className="customer-id-tag"> (ID: {selectedCustomer.id})</span>
                                                )}
                                            </span>
                                            <button
                                                className="change-customer-btn"
                                                onClick={() => {
                                                    setShowReturnFormModal(false);
                                                    setShowCustomerModal(true);
                                                }}
                                            >
                                                Change {activeTab === 'sales' ? 'Customer' : 'Vendor'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowReturnFormModal(false);
                                        setSelectedCustomer(null);
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body">
                                <div className="form-grid-fullwidth">
                                    {/* Transaction Type */}
                                    <div className="form-group-fullwidth">
                                        <label>Transaction Type *</label>
                                        <select
                                            className="form-input-fullwidth"
                                            value={returnForm.transaction_type}
                                            onChange={(e) => handleFormChange('transaction_type', e.target.value)}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="cheque">Cheque</option>
                                            <option value="bank_transfer">Bank Transfer</option>
                                            <option value="upi">UPI</option>
                                        </select>
                                    </div>

                                    {/* Date */}
                                    <div className="form-group-fullwidth">
                                        <label>Date *</label>
                                        <input
                                            type="date"
                                            className="form-input-fullwidth"
                                            value={returnForm.transaction_date}
                                            onChange={e => handleFormChange('transaction_date', e.target.value)}
                                            required
                                        />
                                    </div>

                                    {/* ===================== COMMON SALE FIELDS ===================== */}
                                    {returnForm.transaction_type !== 'select_type' &&
                                        returnForm.transaction_type !== 'opening' && (
                                            <>
                                                {/* Product/Material Selection */}
                                                <div className="form-group-fullwidth">
                                                    <label>{activeTab === 'sales' ? 'Product' : 'Raw Material'} *</label>
                                                    <select
                                                        className="form-input-fullwidth"
                                                        value={returnForm.product}
                                                        onChange={e => handleFormChange('product', e.target.value)}
                                                        required
                                                    >
                                                        <option value="">-- Select {activeTab === 'sales' ? 'Product' : 'Raw Material'} --</option>
                                                        {(activeTab === 'sales' ? products : rawMaterials).map(item => (
                                                            <option key={item.id} value={item.name}>
                                                                {item.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Branch Selection */}
                                                <div className="form-group-fullwidth">
                                                    <label>Branch *</label>
                                                    <select
                                                        className="form-input-fullwidth"
                                                        value={returnForm.branch}
                                                        onChange={e => handleFormChange('branch', e.target.value)}
                                                        required
                                                    >
                                                        <option value="">-- Select Branch --</option>
                                                        {branches.map(b => (
                                                            <option key={b.id} value={b.name}>
                                                                {b.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Quantity & Unit Price - Two columns side by side */}
                                                <div className="form-row-fullwidth">
                                                    <div className="form-group-half">
                                                        <label>Quantity Returned</label>
                                                        <input
                                                            type="text"
                                                            className="form-input-fullwidth"
                                                            value={returnForm.quantity}
                                                            onChange={e => handleFormChange('quantity', e.target.value)}
                                                            min="1"
                                                        />
                                                    </div>

                                                    <div className="form-group-half">
                                                        <label>Unit Price (₹)</label>
                                                        <input
                                                            type="text"
                                                            className="form-input-fullwidth"
                                                            value={returnForm.unit_price}
                                                            onChange={e => handleFormChange('unit_price', e.target.value)}
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Subtotal */}
                                                <div className="form-group-fullwidth">
                                                    <label>Subtotal (₹)</label>
                                                    <input
                                                        type="text"
                                                        className="form-input-fullwidth subtotal"
                                                        value={formatCurrency(returnForm.amount)}
                                                        readOnly
                                                    />
                                                </div>

                                                {/* Discount */}
                                                <div className="form-group-fullwidth">
                                                    <label>Discount (₹)</label>
                                                    <input
                                                        type="text"
                                                        className="form-input-fullwidth"
                                                        value={returnForm.discount}
                                                        onChange={e => handleFormChange('discount', e.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </div>

                                                {/* GST */}
                                                <div className="form-group-fullwidth">
                                                    <label>GST Option</label>
                                                    <div className="radio-container-fullwidth">
                                                        <label className="radio-label">
                                                            <input
                                                                type="radio"
                                                                name="gstOption"
                                                                checked={returnForm.with_gst === true}
                                                                onChange={() => handleFormChange('with_gst', true)}
                                                            />
                                                            <span>With GST (18%)</span>
                                                        </label>

                                                        <label className="radio-label">
                                                            <input
                                                                type="radio"
                                                                name="gstOption"
                                                                checked={returnForm.with_gst === false}
                                                                onChange={() => handleFormChange('with_gst', false)}
                                                            />
                                                            <span>Without GST</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* Total */}
                                                <div className="form-group-fullwidth">
                                                    <label>Total Amount (₹) *</label>
                                                    <input
                                                        type="text"
                                                        className="form-input-fullwidth total-amount"
                                                        value={formatCurrency(returnForm.total_amount)}
                                                        readOnly
                                                        required
                                                    />
                                                </div>
                                            </>
                                        )}

                                    {/* ===================== OPENING / PAYMENT AMOUNT ===================== */}
                                    {(returnForm.transaction_type === 'opening' ||
                                        returnForm.transaction_type === 'payment' ||
                                        returnForm.transaction_type === 'credit_note') && (
                                            <div className="form-group-fullwidth">
                                                <label>
                                                    {returnForm.transaction_type === 'opening'
                                                        ? 'Opening Balance (₹)'
                                                        : 'Amount (₹)'}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-input-fullwidth"
                                                    value={returnForm.amount}
                                                    onChange={e => handleFormChange('amount', e.target.value)}
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </div>
                                        )}

                                    {/* Amount Paid */}
                                    {returnForm.transaction_type !== 'opening' && (
                                        <div className="form-group-fullwidth">
                                            <label>Amount Paid (₹)</label>
                                            <input
                                                type="text"
                                                className="form-input-fullwidth"
                                                value={returnForm.amount_paid}
                                                onChange={e => handleFormChange('amount_paid', e.target.value)}
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    )}

                                    {/* Balance */}
                                    {returnForm.transaction_type !== 'opening' && (
                                        <div className="form-group-fullwidth">
                                            <label>Balance (₹)</label>
                                            <input
                                                type="text"
                                                className="form-input-fullwidth balance"
                                                value={formatCurrency(returnForm.total_amount - returnForm.amount_paid)}
                                                readOnly
                                            />
                                        </div>
                                    )}

                                    {/* Invoice */}
                                    <div className="form-group-fullwidth">
                                        <label>Invoice Number of Returned Goods</label>
                                        <input
                                            type="text"
                                            className="form-input-fullwidth"
                                            value={returnForm.reference_number}
                                            onChange={e => handleFormChange('reference_number', e.target.value)}
                                        />
                                    </div>

                                    {/* Notes */}
                                    <div className="form-group-fullwidth">
                                        <label>Reason for Return</label>
                                        <textarea
                                            className="form-input-fullwidth textarea-fullwidth"
                                            rows="3"
                                            value={returnForm.notes}
                                            onChange={e => handleFormChange('notes', e.target.value)}
                                            placeholder="Add any additional notes..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer">
                                <button
                                    className="btn btn-cancel"
                                    onClick={() => {
                                        setShowReturnFormModal(false);
                                        setSelectedCustomer(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-save"
                                    onClick={handleSaveSale}
                                >
                                    Save Return for {selectedCustomer?.name}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ---------- PAGINATION ---------- */}
            {displayData.length > 0 && (
                <div className="pagination" style={{ justifyContent: totalPages > 1 ? 'space-between' : 'center', marginTop: '20px' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                        Showing {(currentPage - 1) * ROWS_PER_PAGE + 1} to {Math.min(currentPage * ROWS_PER_PAGE, displayData.length)} of {displayData.length} entries
                    </span>

                    {totalPages > 1 && (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Prev
                            </button>

                            <span>
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div >
    );
};

export default ReturnsManagement;